import { api } from "../api/api";
import { ISubscriptionPlan } from "../redux/subscription/subscription.types";

export interface INewSubscriptionPlanDetails {
  subscription_plan_uuid: string;
  subscription_start_date: string;
  status: string;
}
export interface ISubscriptionPlansResult {
  subscriptionPlans: ISubscriptionPlan[];
  // selectedPlanId: string | null;
  nextSubscriptionPlan: INewSubscriptionPlanDetails | null;
}

export const fetchSubscriptionPlansAsync = async (
  billing_cycle?: "days" | "months" | "years"
): Promise<ISubscriptionPlansResult> => {
  try {
    const query = billing_cycle ? `?status=PUBLISH&billing_cycle=${billing_cycle}` : `?status=PUBLISH`;
    const promises: Promise<any>[] = [
      api.get(`/subscription/get-subscription-plan${query}`),
    ];

    // if (hasNoSubscription) {
    //   promises.push(
    //     api.get(
    //       `/subscription/check-subscription-status?company_outlet_uuid=${company_outlet_uuid}`
    //     )
    //   );
    // }

    const results = await Promise.allSettled(promises);

    const plansRes = results[0];
    const paymentRes = results[1];

    return {
      subscriptionPlans:
        plansRes?.status === "fulfilled" ? plansRes.value.data.data : [],

      nextSubscriptionPlan:
        paymentRes?.status === "fulfilled" ? paymentRes.value.data.data : null,
    };
  } catch (error) {
    console.error("Error fetching subscription plans:", error);

    return {
      subscriptionPlans: [],
      nextSubscriptionPlan: null,
    };
  }
};

export const getUpcomingSubscriptionStatusAsync = async (
  company_outlet_uuid: string,
): Promise<INewSubscriptionPlanDetails | null> => {
  try {
    const response = await api.get(
      `/subscription/check-subscription-status?company_outlet_uuid=${company_outlet_uuid}`,
    );
    const data = await response.data.data;
    return data;
  } catch (error) {
    console.error("Error getting subscription plan:", error);
    return null;
  }
};

export const getSubscriptionPlan = async (
  selectedPlanType: string = "All",
  subscription_plan_uuid?: string,
  onCallBack?: (data: any, isSuccess: boolean) => void,
) => {
  try {
    if (selectedPlanType === "All") {
      let url = `/subscription/get-subscription-plan`;

      if (subscription_plan_uuid) {
        url += `?subscription_plan_uuid=${subscription_plan_uuid}`;
      }

      const response = await api.get(url, {});
      const data = await response.data.data;
      return data;
    } else {
      let url = `/subscription/get-subscription-plan?validity_unit=${selectedPlanType}`;

      if (subscription_plan_uuid) {
        url += `&subscription_plan_uuid=${subscription_plan_uuid}`;
      }

      const response = await api.get(url);
      const data = await response.data.data;
      onCallBack?.(data, true);
      return data;
    }
  } catch (error) {
    onCallBack?.(error, false);
    console.error("Error getting subscription plan:", error);
    return null;
  }
};

export const upsertUserSubscription = async (
  subscription_plan_uuid: string,
  company_outlet_uuid: string,
  status: "TRIAL" | "PAID" | string,
) => {
  try {
    const response = await api.post(
      `/subscription/upsert-company-outlet-subscription`,
      {
        company_outlet_uuid,
        subscription_plan_uuid,
        status,
      },
    );

    return response;
  } catch (error: any) {
    // If error response exists and status is 404, return the error response
    if (error.response && error.response.status === 404) {
      return error.response;
    }
    console.error("Error upserting user subscription:");
    return null;
  }
};

interface IUpsertSubscriptionFeature {
  subscription_plan_uuid: string;
  module_uuid: string;
  module_name: string;
  write_count_limit: number;
  status: string;
}

export const upsertSubscriptionFeature = async (
  data: IUpsertSubscriptionFeature,
  session?: any,
) => {
  try {
    const response = await api.post(
      `/subscription/upsert-subscription-feature`,
      data,
    );
    return response;
  } catch (error) {
    console.error("Error upserting subscription feature:", error);
    return null;
  }
};

export const getSubscriptionPlanByUuid = async (
  subscription_plan_uuid: string,
  session?: any,
) => {
  const response = await api.get(
    `https://landmarkapidev.hops7.com/api/v1/general/get-priority-list?reference_column_name=subscription_plan_uuid&reference_column_uuid=${subscription_plan_uuid}&module_name=SUBSCRIPTION_PLAN_FEATURE`,
  );
  return response;
};

interface IEditUserSubscription {
  subscription_plan_uuid: string | null;
  plan_name: string;
  description: string;
  validity_value: number;
  validity_unit: string;
  price: number;
  status: string;
}
export const editSubscriptionPlan = async (
  data: IEditUserSubscription,
  session?: any,
) => {
  try {
    const response = await api.post(
      `/subscription/upsert-subscription-plan`,
      data,
    );
    return response;
  } catch (error) {
    console.error("Error upserting user subscription:", error);
    return null;
  }
};

// Company Outlet Subscription Plan actions

export const getCompanyOutletSubscriptionPlan = async (
  selectedPlanType: string = "All",

  company_outlet_uuid?: string,
  onCallBack?: (data: any, isSuccess: boolean) => void,
) => {
  try {
    if (selectedPlanType === "All") {
      let url = `/subscription/get-company-outlet-subscription?get_active_plan=true`;

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const response = await api.get(url, {});
      const data = await response.data.data;
      return data;
    } else {
      let url = `/subscription/get-company-outlet-subscription?get_active_plan=true&validity_unit=${selectedPlanType}`;

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      const response = await api.get(url);
      const data = await response.data.data;
      onCallBack?.(data, true);
      return data;
    }
  } catch (error) {
    onCallBack?.(error, false);
    console.error("Error getting subscription plan:", error);
    return null;
  }
};

interface IUpserCompanyOutletSubscriptionPlanPayload {
  // subscription_plan_uuid: string | null;
  req_plan_id: string | null;
  company_outlet_uuid: string;
  customer_requested_status: "TRIAL_RQ" | "SUBSCRIPTION_RQ";
  /** How the customer is billed. Defaults to RECURRING on the client. */
  billing_mode?: "RECURRING" | "PREPAID";
  addons?: Array<{
    addon_name: string;
    addon_price: number;
    feature_key: string;
    addon_count: number;
  }>;
}
export const upserCompanyOutletSubscriptionPlan = async (payload: IUpserCompanyOutletSubscriptionPlanPayload) => {
  try {
    const response = await api.post(
      `/subscription/upsert-company-outlet-subscription`,
      payload,
    );

    return response;
  } catch (error: any) {
    // If error response exists and status is 404, return the error response
    if (error.response && error.response.status === 404) {
      return error.response;
    }
    console.error("Error upserting user subscription:");
    return null;
  }
};

// WhatsApp connection status API
export const getWhatsAppConnectionStatus = async (
  company_outlet_uuid: string,
) => {
  try {
    const response = await api.get(
      `/conversation/status?reference_column_uuid=${company_outlet_uuid}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error getting WhatsApp connection status:", error);
    return null;
  }
};

// for qr meta

interface IGetWhatsAppStatusResponse {
  message: string;
  data: {
    use_meta_service: boolean;
  };
}

export const getWhatsAppStatusAsync = async (
  company_outlet_uuid: string,
  onCallBack?: (data: IGetWhatsAppStatusResponse, isSuccess: boolean) => void,
) => {
  try {
    const response = await api.get(
      `/conversation/get-whatsapp-status?company_outlet_uuid=${company_outlet_uuid}`,
    );

    onCallBack?.(response.data, true);
    return response.data;
  } catch (error) {
    onCallBack?.(error as IGetWhatsAppStatusResponse, false);
    console.error("Error getting WhatsApp status:", error);
    return null;
  }
};

// Utility function to get the correct whatsapp_web_service value for API calls
export const getWhatsappWebServiceValue = (
  useMetaService: boolean | null,
  useQRSend: boolean,
): boolean => {
  if (useMetaService === false) return true;

  if (useMetaService === true) return useQRSend;

  // Default to Meta (false) if loading or unknown state when toggle should be visible
  return false;
};

export const copySubscriptionPlan = async (subscription_plan_uuid: string) => {
  try {
    const response = await api.post(`/subscription/copy-subscription-plan`, {
      subscription_plan_uuid,
    });

    return response;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return error.response;
    }
    console.error("Error copying subscription plan:");
    return null;
  }
};

// ── Billing mode change (OTP-gated) ──────────────────────────────────────────

export type BillingMode = "RECURRING" | "PREPAID";

export interface ISendBillingModeOtpPayload {
  otp_for: "BILLING_MODE_CHANGE";
  user_uuid: string;
  /** Sent blank; OTP is delivered to the registered owner's mobile via WhatsApp. */
  email: string;
  mobile: string;
  channel: "EMAIL" | "WHATSAPP";
  valid_minutes: number;
}

export interface ISendBillingModeOtpResponse {
  message: string;
  data: {
    otp_for: string;
    channel: string;
    user_uuid: string;
    otp: string;
  };
}

export interface ISwitchBillingModePayload {
  company_outlet_uuid: string;
  billing_mode: BillingMode;
  otp: string;
}

export interface ISwitchBillingModeResponseData {
  company_outlet_uuid: string;
  previous_billing_mode: BillingMode;
  billing_mode: BillingMode;
  plan_id: string;
  subscription_plan_uuid: string;
  addon_uuids: string[];
  subscription_end_date: string;
  cancelled_subscription_id: string;
  gateway_subscription_id: string;
  rzp_type: "none" | "subscription" | "checkout";
  rzp_order: Record<string, any>;
  rzp_key: string;
}

export interface ISwitchBillingModeResponse {
  message: string;
  data: ISwitchBillingModeResponseData;
}

/** Sends an OTP for the billing-mode change. Defaults to the WhatsApp channel. */
export const sendBillingModeOtpAsync = async (
  payload: ISendBillingModeOtpPayload,
  onCallBack?: (data: ISendBillingModeOtpResponse | any, isSuccess: boolean) => void,
) => {
  try {
    const response = await api.post(`/general/send-otp`, payload);
    onCallBack?.(response.data, true);
    return response.data as ISendBillingModeOtpResponse;
  } catch (error: any) {
    onCallBack?.(error?.response?.data ?? error, false);
    console.error("Error sending billing-mode OTP:", error);
    return null;
  }
};

/** Switches the billing mode after OTP verification. */
export const switchBillingModeAsync = async (
  payload: ISwitchBillingModePayload,
  onCallBack?: (data: ISwitchBillingModeResponse | any, isSuccess: boolean) => void,
) => {
  try {
    const response = await api.post(`/subscription/switch-billing-mode`, payload);
    onCallBack?.(response.data, true);
    return response.data as ISwitchBillingModeResponse;
  } catch (error: any) {
    onCallBack?.(error?.response?.data ?? error, false);
    console.error("Error switching billing mode:", error);
    return null;
  }
};
